/**
 * Account and restricted sections on us.elect.info (electinfo/sites#270, #305).
 *
 * The account box itself is the shared elect.info identity bar
 * (/identity/identity-bar.js from sites/shared/identity/, the same file
 * us.elect.news uses): sign-in panel, email, entitlement chips, Request access,
 * the data use agreement, Log out. This script loads it and does the
 * us.elect.info parts on top:
 *
 *   - restricted section cards (data-premium-href) open for visitors entitled to
 *     that section (voters, individuals, blocks), and otherwise offer Log in
 *     (signed out) or Request access (signed in);
 *   - the 403 page for restricted paths (premium-required.html) says the same;
 *   - the visitor token is refreshed when the tier or entitlements change;
 *   - the global cycle facet (src/cycle-facet.ts) is registered in the bar;
 *   - so is the Graph control on pages without the graph pop-over (src/graph-control.ts);
 *   - profile pages with news get Follow + "News on elect.news" (src/follow-control.ts, sites#435).
 *
 * nginx enforces the same entitlements on those paths, so this only changes
 * what the UI offers.
 *
 * Bundled by build.js to /js/auth.js. Exposes window.electinfoAuth, a promise
 * of { email, elevated, entitlements } on hosts with Authentik (email '' when
 * signed out), or null when Authentik is off or unreachable.
 */

import { initCycleFacet } from './cycle-facet';
import { initFollowControl } from './follow-control';
import { initGraphPlaceholder } from './graph-control';

export interface Entitlements {
  voters?: boolean;
  individuals?: boolean;
  blocks?: boolean;
  curator?: boolean;
}

export interface AuthState {
  email: string;
  elevated: boolean;
  entitlements: Entitlements;
}

interface Identity {
  enabled: string;
  email: string;
  entitlements: Entitlements;
}

interface IdentityUi {
  signInLink: (text: string, cls: string, page?: string) => HTMLAnchorElement;
  openRequest: (area?: string) => boolean;
  emailSignInUrl: (page?: string) => string;
}

type IdentityHost = {
  electinfoIdentity?: Promise<Identity | null>;
  electinfoIdentityUi?: IdentityUi;
  electinfoAuth?: Promise<AuthState | null>;
  __electinfoVisitorToken?: {
    refresh: () => Promise<void>;
    tier: () => string | null;
    entitlements: () => string | null;
  };
};

const host = window as unknown as IdentityHost;
const BAR_SRC = '/identity/identity-bar.js';

// The restricted sections and the entitlement each needs.
const SECTION_AREA: Record<string, keyof Entitlements> = {
  '/voters/': 'voters',
  '/individuals/': 'individuals',
  '/blocks/': 'blocks',
};

/** Load the shared identity bar once (baked pages and generated ones alike). */
function loadBar(): Promise<Identity | null> {
  if (host.electinfoIdentity) return host.electinfoIdentity;
  return new Promise(resolve => {
    let script = document.querySelector<HTMLScriptElement>(`script[src^="${BAR_SRC}"]`);
    const done = (): void => resolve(host.electinfoIdentity ?? Promise.resolve(null));
    if (!script) {
      script = document.createElement('script');
      script.src = BAR_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', () => resolve(null), { once: true });
  });
}

function areaOf(href: string): keyof Entitlements | undefined {
  const path = new URL(href, window.location.href).pathname;
  return SECTION_AREA[path];
}

/**
 * Restricted section cards carry data-premium-href. Entitled: turn the card
 * into a link to its section. Otherwise put Log in or Request access ahead of
 * the premium@ contact.
 */
function updatePremiumCards(me: Identity, ui: IdentityUi | undefined): void {
  document.querySelectorAll<HTMLElement>('[data-premium-href]').forEach(card => {
    const href = card.dataset.premiumHref || '';
    const area = href ? areaOf(href) : undefined;
    if (area && me.entitlements[area] === true) {
      const link = document.createElement('a');
      for (const attr of Array.from(card.attributes)) {
        link.setAttribute(attr.name, attr.value);
      }
      link.href = href;
      link.classList.remove('entity-card--disabled');
      while (card.firstChild) link.appendChild(card.firstChild);
      link.querySelector('.premium-cta')?.remove();
      card.replaceWith(link);
      return;
    }
    const cta = card.querySelector('.premium-cta');
    if (!cta || cta.querySelector('.premium-login') || !ui) return;
    const contact = document.createElement('a');
    contact.href = 'mailto:premium@elect.info';
    contact.textContent = 'premium@elect.info';
    cta.textContent = '';
    if (!me.email) {
      // Come back to the section itself once signed in.
      const login = ui.signInLink('Log in', 'premium-login', new URL(href, window.location.href).href);
      cta.append(login, ' and request access, or contact ', contact);
    } else {
      const request = document.createElement('a');
      request.className = 'premium-login';
      request.href = '#request-access';
      request.textContent = 'Request access';
      request.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        ui.openRequest(area);
      });
      cta.append(request, ' or contact ', contact);
    }
  });
}

/** The 403 page for restricted paths (premium-required.html). */
function updatePremiumNotice(me: Identity, ui: IdentityUi | undefined): void {
  const notice = document.getElementById('premium-required');
  if (!notice) return;
  const status = notice.querySelector<HTMLElement>('.premium-status');
  const login = notice.querySelector<HTMLAnchorElement>('.premium-login');
  const path = window.location.pathname.toLowerCase();
  const area: keyof Entitlements | undefined = path.startsWith('/voters/') ? 'voters'
    : path.startsWith('/individuals/') ? 'individuals'
      : /^\/(?:geo\/)?blocks\//.test(path) ? 'blocks' : undefined;
  if (!me.email) {
    if (login && ui) login.replaceWith(ui.signInLink('Log in', 'premium-login'));
    return;
  }
  if (status) status.textContent = `You are signed in as ${me.email}. This section needs access you do not have yet.`;
  if (login && ui) {
    login.textContent = 'Request access';
    login.href = '#request-access';
    login.addEventListener('click', event => {
      event.preventDefault();
      ui.openRequest(area);
    });
  }
}

function tokenKey(e: Entitlements): string {
  return ['voters', 'individuals', 'blocks'].map(k => (e[k as keyof Entitlements] ? 't' : 'f')).join('');
}

async function start(): Promise<AuthState | null> {
  const me = await loadBar();
  if (!me || me.enabled !== 'true') return null;
  const ui = host.electinfoIdentityUi;
  const apply = (): void => {
    updatePremiumCards(me, ui);
    updatePremiumNotice(me, ui);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  const ent = me.entitlements || {};
  const elevated = Boolean(me.email) && Boolean(ent.voters || ent.individuals || ent.blocks);
  // The visitor token carries the tier and entitlements it was issued for
  // (sites#277, #305): after signing in or out, or a new grant, fetch a new one.
  const token = host.__electinfoVisitorToken;
  const wantTier = elevated ? 'premium' : me.email ? 'member' : 'anon';
  const wantKey = elevated ? tokenKey(ent) : 'fff';
  if (token && token.tier() !== null && (token.tier() !== wantTier || (token.entitlements() ?? wantKey) !== wantKey)) {
    void token.refresh();
  }
  return { email: me.email || '', elevated, entitlements: ent };
}

// Once per page: baked pages include the script themselves and nginx adds it
// to generated ones, so a page could carry it twice.
host.electinfoAuth ??= start();
// The global cycle facet in the identity bar (sites#194, #355).
initCycleFacet();
// The Graph control on pages without the graph pop-over (sites#355).
initGraphPlaceholder();
// Follow on candidate, committee and party pages with news (sites#435), once
// per page like electinfoAuth.
const startFollow = (): void => {
  const w = window as unknown as { __electinfoFollow?: Promise<unknown> };
  w.__electinfoFollow ??= initFollowControl();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startFollow, { once: true });
else startFollow();
